"use client";

import React, { useRef } from "react";
import { Code as Code2, ShoppingBag, Building2, MapPin, Globe as Globe2, FileSearch, Sparkles, KeyRound, FileCode2, Compass, Layers, ArrowUpRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const servicesList = [{
  title: "Technical SEO",
  description: "Fix crawl errors, site architecture, Core Web Vitals, and indexing issues to help search engines find and index your pages.",
  to: "/TechnicalSeoPage",
  badge: "Core Service",
  icon: "code",
  highlight: "Hands-on audit & code fixes"
}, {
  title: "Ecommerce SEO",
  description: "Structure product catalogues, category pages, and faceted navigation to turn organic searches into sales.",
  to: "/EcommerceSeoPage",
  badge: "Revenue Growth",
  icon: "shopping-bag",
  highlight: "Product & category ranking"
}, {
  title: "B2B SEO & Lead Generation",
  description: "Target high-intent decision makers and create bottom-of-funnel search visibility for qualified inbound leads.",
  to: "/B2BSeoLeadGenerationPage",
  badge: "Pipeline",
  icon: "building",
  highlight: "High-intent buyer intent"
}, {
  title: "Local SEO",
  description: "Improve visibility in Google Maps and local search results across target towns, cities, and regional catchment areas.",
  to: "/LocalSEOPage",
  badge: "Map Pack & Local",
  icon: "map-pin",
  highlight: "Google Business Profile & citations"
}, {
  title: "International SEO",
  description: "Expand into global markets with correct hreflang mapping, multi-lingual architecture, and country-level targeting.",
  to: "/InternationalSeoPage",
  badge: "Multi-Market",
  icon: "globe",
  highlight: "Hreflang & regional targeting"
}, {
  title: "SEO Audits",
  description: "Receive a thorough, zero-jargon technical and strategic review identifying what is blocking your organic growth.",
  to: "/SeoAuditsPage",
  badge: "Comprehensive",
  icon: "file-search",
  highlight: "Prioritised action roadmap"
}, {
  title: "AI Search Optimisation",
  description: "Position your brand for modern generative search, AI Overviews, and conversational discovery platforms.",
  to: "/AISearchOptimisationPage",
  badge: "Modern Search",
  icon: "sparkles",
  highlight: "Entity & AI Overviews readiness"
}, {
  title: "Keyword Research",
  description: "Find the exact search terms your buyers use, mapped precisely to commercial intent and search volume.",
  to: "/ServicesPage",
  badge: "Foundational",
  icon: "key",
  highlight: "Search intent & difficulty mapping"
}, {
  title: "On-Page SEO",
  description: "Refine headers, metadata, internal links, and page layouts so search engines and visitors understand your value.",
  to: "/ServicesPage",
  badge: "Optimisation",
  icon: "file-code",
  highlight: "Content & metadata alignment"
}, {
  title: "SEO Strategy",
  description: "Get a clear 6-to-12 month roadmap tailored to your specific business model, competitors, and growth targets.",
  to: "/ServicesPage",
  badge: "Strategic Plan",
  icon: "compass",
  highlight: "Custom milestone roadmap"
}, {
  title: "Content Strategy",
  description: "Build content hubs and topic clusters that establish genuine authority and capture search traffic at every step.",
  to: "/ServicesPage",
  badge: "Authority",
  icon: "layers",
  highlight: "Topic clusters & search intent"
}];
function getServiceIcon(iconType: string) {
  const iconClass = "size-5 text-primary";
  switch (iconType) {
    case "code":
      return <Code2 className={iconClass} />;
    case "shopping-bag":
      return <ShoppingBag className={iconClass} />;
    case "building":
      return <Building2 className={iconClass} />;
    case "map-pin":
      return <MapPin className={iconClass} />;
    case "globe":
      return <Globe2 className={iconClass} />;
    case "file-search":
      return <FileSearch className={iconClass} />;
    case "sparkles":
      return <Sparkles className={iconClass} />;
    case "key":
      return <KeyRound className={iconClass} />;
    case "file-code":
      return <FileCode2 className={iconClass} />;
    case "compass":
      return <Compass className={iconClass} />;
    case "layers":
      return <Layers className={iconClass} />;
    default:
      return <Sparkles className={iconClass} />;
  }
}
export default function Services() {
  const containerRef = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".service-card-anim", {
        y: 24
      });
      gsap.to(".service-card-anim", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.06,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".service-card-anim", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="servicesgrid" ref={containerRef} data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground border-b border-border/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground">
            Comprehensive SEO Capabilities
          </Badge>
          <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-foreground">
            My SEO Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            Comprehensive SEO services designed to resolve technical issues, improve search rankings, and drive sustainable organic growth.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesList.map((service, i) => <div key={service.title} data-index={i} className="service-card-anim flex flex-col h-full" style={{
          opacity: 0
        }}>
              <Card className="flex flex-col justify-between h-full bg-card border border-border shadow-xs hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] duration-200 rounded-xl p-6">
                <div>
                  <CardHeader className="p-0 mb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex size-11 items-center justify-center rounded-lg bg-muted border border-border">
                        {getServiceIcon(service.icon)}
                      </div>
                      <Badge variant="outline" className="text-[12.5px] font-medium border-border/80 text-muted-foreground bg-background">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="font-serif text-xl font-bold text-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="font-sans text-sm text-muted-foreground mt-2 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-0 pt-2 mb-6">
                    <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{service.highlight}</span>
                    </div>
                  </CardContent>
                </div>

                <div className="pt-2 border-t border-border/50">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-sm font-semibold text-primary hover:text-primary hover:bg-muted/70 group">
                    <Link to={service.to}>
                      <span>Explore {service.title}</span>
                      <ArrowUpRight className="size-4 text-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </div>)}
        </div>

        {/* Section Bottom Callout */}
        <div className="mt-14 rounded-2xl bg-muted/50 border border-border p-8 text-center sm:flex sm:items-center sm:justify-between sm:text-left">
          <div className="max-w-xl">
            <h3 className="font-serif text-lg font-semibold text-foreground">
              Unsure which SEO service fits your current stage?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Tell me about your website and growth goals. I will review your organic search visibility and propose a direct plan.
            </p>
          </div>
          <div className="mt-6 sm:mt-0 sm:shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}